import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Bold, Italic, List, ListOrdered, Heading2, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TIPTAP_EXTENSIONS } from './MergeVariableExtension';
import { MergeFieldPicker } from './MergeFieldPicker';
import { Button } from '@/components/ui/Button';

// ---- Props ----

interface TemplateEditorProps {
  initialJson: string | null;
  channel: 'email' | 'sms';
  eventType: string;
  onSave: (json: string, subject?: string) => void;
  isSystem: boolean;
  templateKey: string;
}

// ---- Toolbar button ----

interface ToolbarButtonProps {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}

function ToolbarButton({ active = false, onClick, title, children, disabled = false }: ToolbarButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'p-1.5 rounded transition-colors',
        active ? 'bg-line text-ink' : 'text-ink-soft hover:bg-sunken',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      {children}
    </button>
  );
}

// ---- TemplateEditor ----

export function TemplateEditor({
  initialJson,
  channel,
  eventType,
  onSave,
  isSystem,
  templateKey,
}: TemplateEditorProps): React.ReactElement {
  // Parse initial content safely
  let initialContent: object | string = '';
  if (initialJson !== null && initialJson !== '') {
    try {
      initialContent = JSON.parse(initialJson) as object;
    } catch {
      initialContent = initialJson;
    }
  }

  const [subject, setSubject] = useState('');

  const editor = useEditor({
    extensions: TIPTAP_EXTENSIONS,
    content: initialContent,
    editable: !isSystem,
  });

  // Keep editable in sync with isSystem (if prop changes)
  useEffect(() => {
    if (editor !== null) {
      editor.setEditable(!isSystem);
    }
  }, [editor, isSystem]);

  const charCount = editor?.storage['characterCount']?.characters() as number | undefined ?? 0;
  const smsOverLimit = channel === 'sms' && charCount > 160;

  function handleSave(): void {
    if (editor === null) return;
    onSave(JSON.stringify(editor.getJSON()), channel === 'email' ? subject : undefined);
  }

  return (
    <div className="flex flex-col h-full">
      {/* System template read-only banner */}
      {isSystem && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-warn-soft border-b border-warn-soft text-sm text-warn">
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span>
            System template — click <strong>Customize</strong> to create your own editable version.
          </span>
        </div>
      )}

      {/* Subject line (email only) */}
      {channel === 'email' && (
        <div className="px-4 py-3 border-b border-line">
          <label className="block text-xs font-medium text-ink-muted mb-1" htmlFor={`subject-${templateKey}`}>
            Subject line
          </label>
          <input
            id={`subject-${templateKey}`}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSystem}
            placeholder="Enter email subject..."
            className="w-full text-sm border border-line rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand disabled:bg-bg disabled:text-ink-muted"
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-line flex-wrap">
        {/* Bold / Italic always available */}
        <ToolbarButton
          title="Bold"
          active={editor?.isActive('bold') ?? false}
          disabled={isSystem}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" aria-hidden />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor?.isActive('italic') ?? false}
          disabled={isSystem}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" aria-hidden />
        </ToolbarButton>

        {/* Email-only: headings and lists */}
        {channel === 'email' && (
          <>
            <div className="w-px h-5 bg-line mx-1" aria-hidden />
            <ToolbarButton
              title="Heading"
              active={editor?.isActive('heading', { level: 2 }) ?? false}
              disabled={isSystem}
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            >
              <Heading2 className="h-4 w-4" aria-hidden />
            </ToolbarButton>
            <ToolbarButton
              title="Bullet list"
              active={editor?.isActive('bulletList') ?? false}
              disabled={isSystem}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              <List className="h-4 w-4" aria-hidden />
            </ToolbarButton>
            <ToolbarButton
              title="Ordered list"
              active={editor?.isActive('orderedList') ?? false}
              disabled={isSystem}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            >
              <ListOrdered className="h-4 w-4" aria-hidden />
            </ToolbarButton>
          </>
        )}

        <div className="w-px h-5 bg-line mx-1" aria-hidden />

        {/* Merge field picker */}
        <MergeFieldPicker editor={isSystem ? null : editor ?? null} eventType={eventType} />

        {/* SMS character count */}
        {channel === 'sms' && (
          <span
            className={cn(
              'ml-auto text-xs font-medium tabular-nums',
              smsOverLimit ? 'text-fail' : 'text-ink-muted',
            )}
            aria-live="polite"
            aria-label={`${charCount} of 160 characters used`}
          >
            {charCount} / 160
            {smsOverLimit && (
              <span className="ml-1.5 text-fail font-semibold" aria-hidden>
                Over limit
              </span>
            )}
          </span>
        )}
      </div>

      {/* Editor content area */}
      <div className="flex-1 overflow-y-auto">
        <EditorContent
          editor={editor}
          className={cn(
            'prose prose-sm max-w-none px-4 py-3 min-h-[200px] focus-within:outline-none',
            isSystem && 'opacity-70 bg-bg/60',
          )}
        />
      </div>

      {/* Save button (hidden for system templates) */}
      {!isSystem && (
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line">
          <Button size="sm" onClick={handleSave}>
            Save Version
          </Button>
        </div>
      )}
    </div>
  );
}
